import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import LogProductionModal from './LogProductionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAN_LOG_ROLES = ['grower', 'growing_supervisor', 'owner'];

/**
 * Compact "Log Harvest" card meant for the home / overview screen of
 * Owner, Grower, and Growing Supervisor dashboards.
 *
 * - Shows today's harvest rooms (Flush 1 / Flush 2) as quick info chips
 * - One-click "Log Harvest" button opens the modal, restricted to those
 *   harvest-ready rooms in the dropdown
 */
const LogHarvestCard = ({ userRole = 'owner' }) => {
  const [rooms, setRooms] = useState([]);
  const [activeCycles, setActiveCycles] = useState([]);
  const [harvestRooms, setHarvestRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const canLog = CAN_LOG_ROLES.includes(userRole);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, cyclesRes, supervisorRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles?status=active`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${API}/supervisor/today-work`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { production_rooms: [] } })),
      ]);
      setRooms(roomsRes.data || []);
      const cyclesData = Array.isArray(cyclesRes.data)
        ? cyclesRes.data
        : cyclesRes.data.cycles || [];
      setActiveCycles(cyclesData);
      setHarvestRooms(supervisorRes.data.production_rooms || []);
    } catch (err) {
      // Silent — card just won't show today's harvest list
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!canLog) return null;

  return (
    <>
      <Card
        className="border-[#1A4D2E]/20 bg-gradient-to-r from-[#1A4D2E]/5 to-transparent"
        data-testid="log-harvest-card"
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="rounded-full bg-[#1A4D2E] text-white p-2 shrink-0">
                <Package className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Log Today's Harvest</h3>
                {harvestRooms.length === 0 ? (
                  <p className="text-xs text-gray-500 mt-1">
                    No rooms in Flush phase right now
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-1 mt-1.5">
                    {harvestRooms.slice(0, 4).map((r) => (
                      <Badge
                        key={r.room_cycle_id}
                        variant="outline"
                        className={`text-[10px] ${
                          r.flush === 'Flush 1'
                            ? 'bg-yellow-50 text-yellow-800 border-yellow-300'
                            : 'bg-orange-50 text-orange-800 border-orange-300'
                        }`}
                      >
                        {r.room_name} · {r.flush}
                      </Badge>
                    ))}
                    {harvestRooms.length > 4 && (
                      <span className="text-[10px] text-gray-500">
                        +{harvestRooms.length - 4} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={() => setShowModal(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D23] text-white"
              size="sm"
              disabled={harvestRooms.length === 0}
              data-testid="open-log-harvest-modal"
            >
              <Plus className="w-4 h-4 mr-1" />
              Log Harvest
            </Button>
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <LogProductionModal
          rooms={rooms}
          activeCycles={activeCycles}
          harvestOnly={true}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            toast.success('Harvest logged');
            fetchData();
          }}
        />
      )}
    </>
  );
};

export default LogHarvestCard;
